// External dependencies
import PropTypes from 'prop-types';
import _ from 'lodash';

// Internal dependencies
import { IsEmpty } from './checks';
import Constants from './constants';

const DefaultAcceptedImageTypes = ['png', 'jpg', 'jpeg'];

const CreateStableFile = async (file) => {
  const buffer = await file.arrayBuffer();

  const stableFile = new File(
    [buffer],
    file.name,
    {
      type:         file.type,
      lastModified: file.lastModified,
    },
  );

  return {
    file: stableFile,
    name: stableFile.name,
    url:  URL.createObjectURL(stableFile),
  };
};

const SelectPhotos = async (props) => {
  const files = await (new Promise((resolve) => {
    const input = document.createElement('input');

    input.type = 'file';

    const { acceptedTypes = DefaultAcceptedImageTypes } = props;

    input.accept = [
      ..._.map(acceptedTypes, (t) => `.${t}`),
      ..._.map(acceptedTypes, (t) => `image/${t}`),
    ].join(',');

    if (props.isMultiple) {
      input.multiple = true;
    }

    if (props.useCamera) {
      input.setAttribute('capture', 'environment');
    }

    input.onchange = () => {
      resolve(Array.from(input.files || []));
      input.remove();
    };

    input.oncancel = () => {
      resolve(null);
      input.remove();
    };

    input.click();
  }));

  if ((IsEmpty(files))) {
    return props.isMultiple ? [] : null;
  }

  let selectedFiles = files;

  if (
    props.isMultiple
    && !IsEmpty(props.maxFiles)
    && selectedFiles.length > props.maxFiles
  ) {
    if (!IsEmpty(props.onMaxFilesExceeded)) {
      props.onMaxFilesExceeded({
        maxFiles: props.maxFiles,
        selected: selectedFiles.length,
      });
    }

    selectedFiles = selectedFiles.slice(0, props.maxFiles);
  }

  if (!IsEmpty(props.maxFileSize)) {
    const maxFileSizeInBytes = props.maxFileSize * Constants.BinaryOneMBSize;

    const oversizedFile = _.find(selectedFiles,
      (f) => f.size > maxFileSizeInBytes);

    if (oversizedFile) {
      if (!IsEmpty(props.onMaxFileSizeExceeded)) {
        props.onMaxFileSizeExceeded({
          maxFileSize: maxFileSizeInBytes,
          actualSize:  oversizedFile.size,
          fileName:    oversizedFile.name,
        });
      }

      if (!props.isMultiple) {
        return null;
      }

      selectedFiles = _.filter(selectedFiles,
        (file) => file.size <= maxFileSizeInBytes);
    }
  }

  return Promise.all(_.map(selectedFiles, CreateStableFile));
};

SelectPhotos.propTypes = {
  isMultiple:            PropTypes.bool,
  useCamera:             PropTypes.bool,
  maxFiles:              PropTypes.number,
  maxFileSize:           PropTypes.number, // megabytes
  onMaxFileSizeExceeded: PropTypes.func,
  onMaxFilesExceeded:    PropTypes.func,
  acceptedTypes:         PropTypes.arrayOf(PropTypes.string),
};

SelectPhotos.defaultProps = {
  isMultiple:            false,
  useCamera:             false,
  maxFiles:              null,
  maxFileSize:           7,
  onMaxFileSizeExceeded: null,
  onMaxFilesExceeded:    null,
};

export default SelectPhotos;
